import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import {
  answerBlock
} from 'actions';

import Question from 'components/Questionnaires/Question';
import moment from 'moment';
import format from 'date-fns/format';

import { QuestionButtonsField, QuestionUsTinField, QuestionYesNoField, QuestionBusinessNumberField, QuestionDateDropdownField } from 'components/Questionnaires/fields';
import { requiredValidator, lengthValidator, sameCharacterValidator, sequentialIntegerValidator, yearMonthValidator, validateQuestion } from 'components/Questionnaires/validators';
import CollapsibleHeader from 'components/Questionnaires/CollapsibleHeader';

export const QuestionRcUsTaxResidentBlock = ({ block, onAnswer }) => {
  const dispatch = useDispatch();
  const { tinType, showUsBusinessNumber, showResidentSince = true } = block.componentProps ?? {};
  const [answer, setAnswer] = useState(block?.answers ?? { appliedFor: 'No' });
  const [collapsibleAreaShown, setCollapsibleAreaShown] = useState(false);
  const tinValidators = {
    conditions: [
      { condition: requiredValidator, message: 'questionnaire.usTin.tin.errorMessage' },
      { condition: lengthValidator, message: 'questionnaire.usTin.tin.lengthErrorMessage' },
      { condition: sameCharacterValidator, message: 'questionnaire.usTin.tin.sameCharErrorMessage' },
      { condition: sequentialIntegerValidator, message: 'questionnaire.usTin.tin.sequentialErrorMessage' },
    ]
  };

  const [isUsTaxResident, setIsUsTaxResident] = useState({
    id: 'isUsTaxResident',
    label: 'questionnaire.rc518.usTaxResident.isUsTaxResident.label',
    component: QuestionYesNoField,
    visible: true,
    validation: {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.rc518.usTaxResident.isUsTaxResident.errorMessage' }
      ]
    },
    answer: answer?.isUsTaxResident
  });
  const [usTin, setUsTin] = useState({
    id: 'tin',
    component: QuestionUsTinField,
    componentProps: {
      tinFormat: tinType,
      length: 9
    },
    label: 'questionnaire.rc518.usTaxResident.tin.label',
    info: {
      instructions: 'questionnaire.rc518.usTaxResident.tin.info',
    },
    className: 'col-12',
    visible: () => answer?.isUsTaxResident === 'Yes',
    validation: tinValidators,
    answer: answer?.tin
  });
  const appliedFor = {
    id: 'appliedFor',
    label: 'questionnaire.usTin.appliedFor.label',
    component: QuestionButtonsField,
    componentProps: {
      buttons: [
        { value: 'Yes', text: 'questionnaire.buttons.Yes' },
        { value: 'No', text: 'questionnaire.buttons.No' }
      ]
    },
    visible: () => answer?.isUsTaxResident === 'Yes',
    answer: answer?.appliedFor
  };
  const [residentSinceUs, setResidentSinceUs] = useState({
    id: 'residentSinceUs',
    label: 'questionnaire.rc518.usTaxResident.residentSinceUs.label',
    component: QuestionDateDropdownField,
    componentProps: {
      dateFormat: 'yyyy-MM',
      monthYearOnly: true,
      maxDate: format(moment().toDate(), 'yyyy-MM')
    },
    info: {
      instructions: 'questionnaire.rc518.usTaxResident.residentSinceUs.info'
    },
    className: 'col-12 rc-resident-since-date',
    visible: () => showResidentSince && answer?.isUsTaxResident === 'Yes',
    validation: {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.rc.residentSince.requiredErrorMessage' },
        { condition: yearMonthValidator, message: 'questionnaire.rc518.usTaxResident.residentSinceUs.errorMessage' }
      ]
    },
    answer: answer?.residentSinceUs
  });
  const [usBusinessNumber, setUsBusinessNumber] = useState({
    id: 'usBusinessNumber',
    label: 'questionnaire.rc519.usTaxResident.usBusinessNumber.label',
    component: QuestionBusinessNumberField,
    componentProps: {
      length: 15
    },
    className: 'col-12',
    visible: () => showUsBusinessNumber && answer?.isUsTaxResident === 'Yes',
    answer: answer?.usBusinessNumber
  });

  const blockIdentifier = useMemo(() => ((block && block.id) ? `${ block.id }${ block.index !== undefined ? (`.${ block.index }`) : '' }` : undefined), [block.index]);

  const isValid = () => {
    if (answer.isUsTaxResident === 'No') {
      return true;
    }
    if (answer.isUsTaxResident === 'Yes') {
      return usTin.isValid && (!showResidentSince || residentSinceUs.isValid === true) && (!showUsBusinessNumber || usBusinessNumber.isValid !== false);
    }
    return isUsTaxResident.isValid && (usTin.isValid || (!answer.tin && answer.appliedFor === 'Yes'));
  };

  const onQuestionChange = (q, value) => {
    switch (q.id) {
      case 'isUsTaxResident': {
        const newIsUsTaxResident = { ...q, answer: value };
        validateQuestion(newIsUsTaxResident);
        setIsUsTaxResident(newIsUsTaxResident);
        const newComponentProps = { ...usTin.componentProps, ...{ disabled: false } };
        const newUsTin = { ...usTin, answer: undefined, isValid: undefined, errorMessages: undefined, ...{ validation: tinValidators, componentProps: newComponentProps } };
        setUsTin(newUsTin);
        const newResidentSinceUs = { ...residentSinceUs, answer: undefined, isValid: undefined, errorMessages: undefined };
        setResidentSinceUs(newResidentSinceUs);
        const newUsBusinessNumber = { ...usBusinessNumber, answer: undefined, isValid: undefined, errorMessages: undefined };
        setUsBusinessNumber(newUsBusinessNumber);
        setAnswer({ ...answer, isUsTaxResident: value, tin: undefined, residentSinceUs: undefined, usBusinessNumber: undefined, appliedFor: value === 'Yes' ? 'No' : undefined });
        break;
      }
      case 'tin': {
        const newUsTin = { ...q, answer: value };
        validateQuestion(newUsTin);
        setUsTin(newUsTin);
        setAnswer({ ...answer, tin: value });
        break;
      }
      case 'residentSinceUs': {
        const newResidentSinceUs = { ...q, answer: value };
        validateQuestion(newResidentSinceUs);
        setResidentSinceUs(newResidentSinceUs);
        setAnswer({ ...answer, residentSinceUs: value });
        break;
      }
      case 'usBusinessNumber': {
        const newUsBusinessNumber = { ...q, answer: value };
        validateQuestion(newUsBusinessNumber);
        setUsBusinessNumber(newUsBusinessNumber);
        setAnswer({ ...answer, usBusinessNumber: value });
        break;
      }
      case 'appliedFor': {
        const newComponentProps = { ...usTin.componentProps, ...{ disabled: value === 'Yes' } };
        const validators = value === 'Yes' ? null : tinValidators;
        const questionProps = value === 'Yes' ? { isValid: true, errorMessages: undefined } : null;
        const newUsTin = { ...usTin, ...questionProps, ...{ validation: validators, componentProps: newComponentProps } };
        validateQuestion(newUsTin);
        setUsTin(newUsTin);
        setAnswer({ ...answer, appliedFor: value });
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    if (!onAnswer || typeof onAnswer !== 'function') {
      if (Object.keys(answer).length > 0 && answer.constructor === Object) {
        dispatch(answerBlock(block.id, answer, isValid()));
      } else {
        onQuestionChange(isUsTaxResident, undefined);
        onQuestionChange(usTin, undefined);
        onQuestionChange(residentSinceUs, undefined);
        onQuestionChange(usBusinessNumber, undefined);
        onQuestionChange(appliedFor, undefined);
      }
    } else {
      onAnswer(block, answer, isValid());
    }
  }, [answer]);

  useEffect(() => {
    setAnswer(block.answers ?? {});
  }, [block.answers]);

  useEffect(() => {
    if (block.isForceEdited === true) {
      onQuestionChange({ ...isUsTaxResident, isForceEdited: true }, answer?.isUsTaxResident ?? '');
      onQuestionChange({ ...usTin, isForceEdited: true }, answer?.tin ?? '');
      if (showResidentSince && answer?.isUsTaxResident === 'Yes') {
        onQuestionChange({ ...residentSinceUs, isForceEdited: true }, answer?.residentSinceUs ?? '');
      }
    }
  }, [block.isForceEdited]);

  const isUsResident = answer?.isUsTaxResident === 'Yes';

  return (<div className="container row">
    <Question key={ isUsTaxResident.id } blockId={ blockIdentifier } question={ isUsTaxResident } { ...isUsTaxResident.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    { appliedFor.visible() && <Question key={ usTin.id } blockId={ blockIdentifier } question={ usTin } { ...usTin.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } /> }
    { showResidentSince && isUsResident && <Question key={ residentSinceUs.id } blockId={ blockIdentifier } question={ residentSinceUs } { ...residentSinceUs.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } /> }
    { showUsBusinessNumber && isUsResident && <Question key={ usBusinessNumber.id } blockId={ blockIdentifier } question={ usBusinessNumber } { ...usBusinessNumber.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } /> }
    { appliedFor.visible() && <div className="col-12">
      <CollapsibleHeader isShown={ collapsibleAreaShown } onExpandCollapse={ value => setCollapsibleAreaShown(value) } text="questionnaire.usTin.moreOptions" />
      { collapsibleAreaShown && <Question key={ appliedFor.id } blockId={ blockIdentifier } question={ appliedFor } { ...appliedFor.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } /> }
    </div> }
  </div>);
};

QuestionRcUsTaxResidentBlock.displayName = 'QuestionRcUsTaxResidentBlock';

QuestionRcUsTaxResidentBlock.propTypes = {
  block: PropTypes.shape().isRequired,
  onAnswer: PropTypes.func,
};

QuestionRcUsTaxResidentBlock.defaultProps = {
  onAnswer: null
};

export default QuestionRcUsTaxResidentBlock;
